import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import routes from '@/config/routes';
import { FULFILLED_ACTION } from '@/config/variables';
import { formData } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { clearState, sendResetLink } from '@/store/slices/authSlice';
import { schemaResetPassword } from './yup-schemas';
import { Button, Input, PanelLabel, Spinner } from '@/components/common';

interface ISubmitResetPassword {
  username: string;
}

const ResetPassword = (): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isFetching, hasSentPasswordResetLink } = useAppSelector(state => state.auth);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, touchedFields },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: yupResolver(schemaResetPassword),
  });

  const formDataOptions = {
    register,
    errors,
    touchedFields,
  };

  const handleResendEmail = () => {
    dispatch(clearState());
  };

  const handleBackToLogin = () => {
    history.push(routes.auth.login);
  };

  const getErrorMessage = (serverResponse: string): string => {
    if (serverResponse.toLocaleLowerCase() === 'unable to send password reset email') return 'emailError';

    return 'apiError';
  };

  const onSubmit = async (payload: ISubmitResetPassword) => {
    if (payload.username.trim().length > 0) {
      dispatch(sendResetLink(payload)).then((response: any) => {
        if (response.meta.requestStatus !== FULFILLED_ACTION) {
          setError('username', {
            type: 'api',
            message:
              getErrorMessage(response.payload.data) === 'emailError'
                ? t`auth.formResetPassword.validationSchema.unableToSendEmail`
                : t`auth.formResetPassword.validationSchema.apiError`,
          });
        }
      });
    } else {
      setError('username', {
        type: 'required',
        message: t`auth.formNewPassword.validationSchema.required`,
      });
    }
  };

  return (
    <DivContainer>
      <DivBodyContainer className="container">
        <DivResetPasswordForm>
          <PanelLabel>{t`auth.formResetPassword.title`}</PanelLabel>

          <p className="fs-14 mt-3">{t`auth.formResetPassword.passwordMessage`}</p>

          {hasSentPasswordResetLink ? (
            <InnerFormDiv>
              <Disclaimer>{t`auth.formResetPassword.disclaimer`}</Disclaimer>
              <ButtonsContainer>
                <ButtonBackToLogin
                  size="l"
                  onClick={handleBackToLogin}
                  color="primary"
                >{t`auth.btnBackToLogin`}</ButtonBackToLogin>
                <ButtonResendEmail size="l" onClick={handleResendEmail}>{t`auth.btnResendEmail`}</ButtonResendEmail>
              </ButtonsContainer>
              <Disclaimer>{t`auth.formResetPassword.disclaimerNoEmail`}</Disclaimer>
            </InnerFormDiv>
          ) : (
            <FormResetPass onSubmit={handleSubmit(onSubmit)}>
              <Input
                type="text"
                name="username"
                label={t`auth.formResetPassword.labelUsername`}
                {...formData('username', formDataOptions)}
                disabled={isFetching}
              />

              <ButtonSubmit size="l" color="primary" type="submit" disabled={isFetching}>
                {t`auth.formResetPassword.btnSendResetLink`}
              </ButtonSubmit>
              {isFetching && <Spinner size="sm" animation="border" variant="primary" />}
            </FormResetPass>
          )}
        </DivResetPasswordForm>
      </DivBodyContainer>
    </DivContainer>
  );
};

export { ResetPassword };

const InnerFormDiv = styled.div.attrs({ className: 'w-100 flex-grow-1' })`
  max-width: 325px;
  margin: auto;
  margin-top: 50px;
`;

const FormResetPass = styled.form.attrs({ className: 'w-100 flex-grow-1' })`
  max-width: 325px;
  margin: auto;
  margin-top: 50px;
`;
const ButtonSubmit = styled(Button).attrs({ className: 'w-100 mt-2' })``;
const Disclaimer = styled.div.attrs({ className: 'fs-14' })``;
const ButtonsContainer = styled.form.attrs({ className: 'w-100 flex-grow-1' })``;
const ButtonResendEmail = styled(Button).attrs({ className: 'w-100 mt-3' })``;
const ButtonBackToLogin = styled(Button).attrs({ className: 'w-100 mt-3' })``;
const DivContainer = styled.div`
  max-width: 1000px;
  padding: 25px 50px 80px 80px;
  margin: auto;
`;
const DivBodyContainer = styled.div`
  padding: 25px 25px 40px 25px;
  display: flex;
  align-items: center;
  border-radius: 4px;

  margin-top: 40px;
  background-color: #fff;
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  min-width: 0;
`;
const DivResetPasswordForm = styled.div.attrs({ className: 'row' })`
  border: 0px;
`;
